<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="部门日报规则管理">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="closeDrawer"></div>
      <aside class="department-rule-drawer-panel absolute inset-y-0 right-0 flex h-full flex-col bg-white shadow-2xl">
        <header class="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Cog6ToothIcon class="h-5 w-5" />
            </div>
            <div>
              <h2 class="text-lg font-semibold text-slate-900">部门日报规则</h2>
              <p class="mt-0.5 text-sm text-slate-500">配置你作为部门负责人可管理部门的日报规则。</p>
            </div>
          </div>
          <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" aria-label="关闭" @click="closeDrawer">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </header>

        <main class="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-5">
          <section class="grid gap-3 sm:grid-cols-3">
            <div v-for="item in summaryCards" :key="item.label" class="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div class="text-xs font-medium text-slate-500">{{ item.label }}</div>
              <div class="mt-1 text-2xl font-semibold text-slate-900">{{ item.value }}</div>
              <div class="mt-1 text-xs" :class="item.tone">{{ item.hint }}</div>
            </div>
          </section>

          <section class="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div>
                <h3 class="text-base font-semibold text-slate-900">负责部门</h3>
                <p class="mt-0.5 text-sm text-slate-500">所有部门默认继承系统规则，你只需要为负责的部门新增覆盖规则。</p>
              </div>
              <button type="button" class="btn-secondary" :disabled="loading" @click="loadDepartments">
                <ArrowPathIcon class="h-4 w-4" />
                刷新
              </button>
            </div>

            <div v-if="loading" class="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
              <span class="text-sm text-slate-500">正在加载负责部门规则...</span>
            </div>
            <div v-else-if="loadError" class="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
                <ExclamationTriangleIcon class="h-6 w-6 text-rose-500" />
              </div>
              <p class="text-sm text-slate-600">部门规则加载失败，请稍后重试。</p>
              <button type="button" class="btn-secondary mt-1" @click="loadDepartments">重试</button>
            </div>
            <div v-else-if="departmentRows.length === 0" class="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <InboxIcon class="h-6 w-6 text-slate-400" />
              </div>
              <span class="text-sm text-slate-500">暂无可管理的负责部门</span>
            </div>
            <div v-else class="data-table-scroll max-h-[56vh]">
              <table class="data-table">
                <thead class="data-table-head">
                  <tr>
                    <th class="data-table-th text-left">部门</th>
                    <th class="data-table-th text-left">日报功能</th>
                    <th class="data-table-th text-left">计划截止</th>
                    <th class="data-table-th text-left">计划提醒</th>
                    <th class="data-table-th text-left">总结截止</th>
                    <th class="data-table-th text-left">总结提醒</th>
                    <th class="data-table-th text-left">生效区间</th>
                    <th class="data-table-th text-right">操作</th>
                  </tr>
                </thead>
                <tbody class="data-table-body">
                  <tr v-for="row in departmentRows" :key="row.departmentId" class="transition-colors hover:bg-slate-50/80">
                    <td class="data-table-td">
                      <div class="font-medium text-slate-900">{{ row.departmentName || '--' }}</div>
                      <div class="mt-1 text-xs text-slate-500">ID {{ row.departmentId || '--' }}</div>
                    </td>
                    <td class="data-table-td">
                      <span class="status-badge" :class="row.rule?.dailyReportEnabled === false ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'">
                        {{ row.rule?.dailyReportEnabled === false ? '关闭' : '开启' }}
                      </span>
                    </td>
                    <td class="data-table-td text-slate-700">{{ formatRuleTime(row.rule?.planDeadline) }}</td>
                    <td class="data-table-td text-slate-700">{{ formatReminderOffset(row.rule?.teamPlanReminderEnabled, row.rule?.teamPlanReminderOffsetMinutes) }}</td>
                    <td class="data-table-td text-slate-700">{{ formatRuleTime(row.rule?.summaryDeadline) }}</td>
                    <td class="data-table-td text-slate-700">{{ formatReminderOffset(row.rule?.teamSummaryReminderEnabled, row.rule?.teamSummaryReminderOffsetMinutes) }}</td>
                    <td class="data-table-td text-slate-700">{{ formatDateRange(row.rule) }}</td>
                    <td class="data-table-td">
                      <div class="flex justify-end gap-1.5">
                        <button
                          type="button"
                          class="table-action-button is-primary"
                          :disabled="row.editable === false"
                          :title="isInheritedRule(row) ? '配置规则' : '编辑规则'"
                          :aria-label="isInheritedRule(row) ? '配置规则' : '编辑规则'"
                          @click="openEditor(row)"
                        >
                          <PencilSquareIcon class="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </aside>

      <div v-if="editorVisible" class="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="编辑部门日报规则">
        <div class="absolute inset-0 bg-slate-900/40" @click="closeEditor"></div>
        <aside class="department-rule-editor-panel absolute inset-y-0 right-0 flex h-full flex-col bg-white shadow-2xl">
          <header class="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ editorTitle }}</h3>
              <p class="mt-0.5 text-sm text-slate-500">保存后更新该部门的日报规则配置。</p>
            </div>
            <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" aria-label="关闭" @click="closeEditor">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>

          <form class="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-5" @submit.prevent="submitRule">
            <div class="space-y-4">
              <label class="block">
                <span class="form-label">部门</span>
                <input v-model="ruleForm.departmentName" class="form-readonly mt-1 w-full" readonly>
              </label>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block">
                  <span class="form-label">生效日期 <span class="text-red-500">*</span></span>
                  <input v-model="ruleForm.effectiveFrom" class="form-input form-date mt-1 w-full" type="date">
                </label>
                <label class="block">
                  <span class="form-label">失效日期</span>
                  <input v-model="ruleForm.effectiveTo" class="form-input form-date mt-1 w-full" type="date">
                </label>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block">
                  <span class="form-label">计划截止</span>
                  <input v-model="ruleForm.planDeadline" class="form-input mt-1 w-full" type="time">
                </label>
                <label class="block">
                  <span class="form-label">总结截止</span>
                  <input v-model="ruleForm.summaryDeadline" class="form-input mt-1 w-full" type="time">
                </label>
              </div>

              <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                <label class="form-switch">
                  <input v-model="ruleForm.dailyReportEnabled" class="form-switch-input" type="checkbox">
                  <span class="form-switch-track"></span>
                  <span class="form-switch-label">开启团队日报要求</span>
                </label>
                <p class="mt-1 text-xs leading-5 text-slate-500">关闭后该部门不计入团队应交和团队提醒，员工仍可主动提交日报。</p>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <label class="form-switch">
                    <input v-model="ruleForm.teamPlanReminderEnabled" class="form-switch-input" type="checkbox">
                    <span class="form-switch-track"></span>
                    <span class="form-switch-label">团队计划提醒</span>
                  </label>
                  <label class="mt-3 block">
                    <span class="form-label">相对计划截止（分钟）</span>
                    <input v-model.number="ruleForm.teamPlanReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!ruleForm.teamPlanReminderEnabled">
                  </label>
                  <p class="mt-1 text-xs text-slate-500">{{ formatReminderOffset(ruleForm.teamPlanReminderEnabled, ruleForm.teamPlanReminderOffsetMinutes) }}</p>
                </div>
                <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <label class="form-switch">
                    <input v-model="ruleForm.teamSummaryReminderEnabled" class="form-switch-input" type="checkbox">
                    <span class="form-switch-track"></span>
                    <span class="form-switch-label">团队总结提醒</span>
                  </label>
                  <label class="mt-3 block">
                    <span class="form-label">相对总结截止（分钟）</span>
                    <input v-model.number="ruleForm.teamSummaryReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!ruleForm.teamSummaryReminderEnabled">
                  </label>
                  <p class="mt-1 text-xs text-slate-500">{{ formatReminderOffset(ruleForm.teamSummaryReminderEnabled, ruleForm.teamSummaryReminderOffsetMinutes) }}</p>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-5">
              <button type="button" class="btn-secondary" @click="closeEditor">取消</button>
              <button type="submit" class="btn-primary" :disabled="submitting">
                {{ submitting ? '保存中' : '保存规则' }}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  ArrowPathIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  PencilSquareIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import {
  getLeaderDepartmentWorkReportRules,
  saveDepartmentWorkReportRule
} from '@/api/workReport'
import {
  buildDepartmentRulePayload,
  createDepartmentRuleForm,
  formatDateRange,
  formatReminderOffset,
  formatRuleTime,
  isInheritedRule,
  normalizeLeaderDepartmentRules
} from '../composables/workReportDepartmentRules.mjs'
import {
  isValidDateRange,
  isValidDeadlineTime,
  parsePositiveIntegerId
} from '../composables/workReportGovernanceValidation.mjs'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const departmentRows = ref([])
const editingDepartment = ref(null)
const loading = ref(false)
const loadError = ref(false)
const submitting = ref(false)
const editorVisible = ref(false)
const ruleForm = reactive(createDepartmentRuleForm())

const summaryCards = computed(() => {
  const total = departmentRows.value.length
  const inherited = departmentRows.value.filter((row) => isInheritedRule(row)).length
  const configured = Math.max(0, total - inherited)
  return [
    { label: '负责部门', value: total, hint: '当前可管理范围', tone: 'text-slate-500' },
    { label: '部门规则', value: configured, hint: '已覆盖默认规则', tone: 'text-blue-600' },
    { label: '继承系统默认', value: inherited, hint: '使用公共默认规则', tone: 'text-slate-500' }
  ]
})
const editorTitle = computed(() => isInheritedRule(editingDepartment.value) ? '配置规则' : '编辑规则')

watch(() => props.modelValue, (visible) => {
  if (visible) loadDepartments()
})

function closeDrawer() {
  if (submitting.value) return
  emit('update:modelValue', false)
}

async function loadDepartments() {
  loading.value = true
  loadError.value = false
  try {
    departmentRows.value = normalizeLeaderDepartmentRules(await getLeaderDepartmentWorkReportRules())
  } catch {
    departmentRows.value = []
    loadError.value = true
  } finally {
    loading.value = false
  }
}

function openEditor(row) {
  if (row.editable === false) return
  editingDepartment.value = row
  Object.assign(ruleForm, createDepartmentRuleForm(row))
  editorVisible.value = true
}

function closeEditor() {
  if (!submitting.value) editorVisible.value = false
}

async function submitRule() {
  if (!validateRuleForm()) return

  submitting.value = true
  try {
    await saveDepartmentWorkReportRule(buildDepartmentRulePayload(ruleForm))
    ElMessage.success('部门日报规则已保存')
    editorVisible.value = false
    await loadDepartments()
  } catch (error) {
    ElMessage.error(error?.message || '部门日报规则保存失败，请稍后重试')
  } finally {
    submitting.value = false
  }
}

function validateRuleForm() {
  if (parsePositiveIntegerId(ruleForm.departmentId) === null) {
    ElMessage.warning('缺少有效部门')
    return false
  }
  if (parsePositiveIntegerId(ruleForm.versionNo) === null || !ruleForm.effectiveFrom) {
    ElMessage.warning('请填写生效日期')
    return false
  }
  if (!isValidDateRange(ruleForm.effectiveFrom, ruleForm.effectiveTo)) {
    ElMessage.warning('生效日期不能晚于失效日期')
    return false
  }
  if (!isValidDeadlineTime(ruleForm.planDeadline) || !isValidDeadlineTime(ruleForm.summaryDeadline)) {
    ElMessage.warning('请填写有效的截止时间')
    return false
  }
  if (ruleForm.teamPlanReminderEnabled && !Number.isInteger(Number(ruleForm.teamPlanReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的团队计划提醒时间')
    return false
  }
  if (ruleForm.teamSummaryReminderEnabled && !Number.isInteger(Number(ruleForm.teamSummaryReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的团队总结提醒时间')
    return false
  }
  return true
}
</script>

<style scoped>
.department-rule-drawer-panel,
.department-rule-editor-panel {
  width: 100vw;
}

@media (min-width: 640px) {
  .department-rule-drawer-panel,
  .department-rule-editor-panel {
    width: 92vw;
  }
}

@media (min-width: 768px) {
  .department-rule-drawer-panel,
  .department-rule-editor-panel {
    width: 40vw;
  }
}
</style>
